import React, { useState, useEffect } from 'react';
import { X, Heart, CreditCard, DollarSign, Star, CheckCircle, Gift, Plus, Globe, Smartphone } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { saveDonation, type Donation } from '../utils/donations';
import { getPaymentMethods, getDefaultPaymentMethod, maskCardNumber, type PaymentMethod } from '../utils/payments';
import { PaymentForm } from './PaymentForm';
import { useTranslation } from '../hooks/useTranslation';

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DonationAmount {
  value: number;
  labelKey: string;
  descriptionKey: string;
}

export const DonationModal: React.FC<DonationModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState<number>(25);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [donorName, setDonorName] = useState<string>('');
  const [donorEmail, setDonorEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [completedDonation, setCompletedDonation] = useState<Donation | null>(null);
  const [showPaymentPage, setShowPaymentPage] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  const donationAmounts: DonationAmount[] = [
    { value: 5, labelKey: 'donation.amount5', descriptionKey: 'donation.amount5Desc' },
    { value: 10, labelKey: 'donation.amount10', descriptionKey: 'donation.amount10Desc' },
    { value: 25, labelKey: 'donation.amount25', descriptionKey: 'donation.amount25Desc' },
    { value: 50, labelKey: 'donation.amount50', descriptionKey: 'donation.amount50Desc' },
    { value: 100, labelKey: 'donation.amount100', descriptionKey: 'donation.amount100Desc' },
    { value: 0, labelKey: 'donation.customAmount', descriptionKey: 'donation.customAmountDesc' },
  ];

  useEffect(() => {
    if (isOpen) {
      const methods = getPaymentMethods();
      setPaymentMethods(methods);
      const defaultMethod = getDefaultPaymentMethod();
      setSelectedPaymentMethod(defaultMethod);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getFinalAmount = (): number => {
    if (selectedAmount === 0) {
      return parseFloat(customAmount) || 0;
    }
    return selectedAmount;
  };

  const handleDonation = async () => {
    const amount = getFinalAmount();
    
    if (amount < 1) {
      toast.error(t('donation.minimumAmountError'));
      return;
    }

    if (!donorEmail) {
      toast.error(t('donation.emailRequiredError'));
      return;
    }

    if (!selectedPaymentMethod) {
      setShowPaymentPage(true);
      return;
    }

    setIsProcessing(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const donationData = {
        amount,
        donorName: donorName || t('donation.anonymous'),
        donorEmail,
        message,
        isRecurring,
        timestamp: new Date().toISOString(),
      };

      const savedDonation = saveDonation(donationData);
      setCompletedDonation(savedDonation);
      
      setShowThankYou(true);
      toast.success(t('donation.successMessage'));
      
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(t('donation.errorMessage'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setShowThankYou(false);
    setShowPaymentPage(false);
    setSelectedAmount(25);
    setCustomAmount('');
    setDonorName('');
    setDonorEmail('');
    setMessage('');
    setIsRecurring(false);
    setCompletedDonation(null);
    setSelectedPaymentMethod(null);
    onClose();
  };

  const handlePaymentSuccess = (transactionId: string, paymentMethod: PaymentMethod) => {
    const amount = getFinalAmount();
    
    const donationData = {
      amount,
      donorName: donorName || t('donation.anonymous'),
      donorEmail,
      message,
      isRecurring,
      timestamp: new Date().toISOString(),
    };

    const savedDonation = saveDonation(donationData);
    setCompletedDonation(savedDonation);
    
    const updatedMethods = getPaymentMethods();
    setPaymentMethods(updatedMethods);
    setSelectedPaymentMethod(paymentMethod);
    
    setShowPaymentPage(false);
    setShowThankYou(true);
    toast.success(t('donation.successMessage'));
  };

  // Thank You Screen
  if (showThankYou) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('donation.thankYouTitle')}</h2>
            <p className="text-green-100">{t('donation.thankYouSubtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2">{t('donation.impactTitle')}</h3>
                <p className="text-green-700 text-sm">
                  {t('donation.impactMessage', { amount: (completedDonation?.amount || getFinalAmount()).toString() })}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-2 text-purple-600">
                  <Star size={20} />
                  <span className="font-medium">{t('donation.positiveEnergyMessage')}</span>
                  <Star size={20} />
                </div>
                
                <p className="text-gray-600 text-sm">
                  {t('donation.danaPhilosophy')}
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-800 text-xs italic">
                    {t('donation.wisdomQuote')}
                  </p>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
              >
                {t('donation.continueJourney')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment Form Screen
  if (showPaymentPage) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
          <PaymentForm
            amount={getFinalAmount()}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setShowPaymentPage(false)}
            donorInfo={{
              name: donorName,
              email: donorEmail,
              message,
              isRecurring
            }}
          />
        </div>
      </div>
    );
  }

  // Main Donation Form
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-amber-600 text-white p-6 relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('donation.giveWithLove')}</h2>
            <p className="text-purple-100">{t('donation.danaDescription')}</p>
          </div>
        </div>

        <div className="p-6">
          {/* Amount Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('donation.selectAmount')}</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {donationAmounts.map((amount) => (
                <button
                  key={amount.value}
                  onClick={() => setSelectedAmount(amount.value)}
                  className={`p-3 rounded-lg border-2 text-center transition-all duration-200 ${
                    selectedAmount === amount.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-purple-300 text-gray-700'
                  }`}
                >
                  <div className="font-semibold">{amount.value === 0 ? t(amount.labelKey) : `$${amount.value}`}</div>
                  <div className="text-xs text-gray-500 mt-1">{t(amount.descriptionKey)}</div>
                </button>
              ))}
            </div>

            {/* Custom Amount Input */}
            {selectedAmount === 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('donation.enterCustomAmount')}
                </label>
                <div className="relative">
                  <DollarSign size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Recurring Option */}
          <div className="mb-6">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                className="w-4 h-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <div>
                <span className="font-medium text-gray-900">{t('donation.makeRecurring')}</span>
                <p className="text-sm text-gray-500">{t('donation.recurringDescription')}</p>
              </div>
            </label>
          </div>

          {/* Donor Information */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('donation.yourName')} ({t('donation.optional')})
              </label>
              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder={t('donation.namePlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('donation.emailAddress')} *
              </label>
              <input
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                placeholder={t('donation.emailPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('donation.personalMessage')} ({t('donation.optional')})
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('donation.messagePlaceholder')}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Payment Method Display */}
          {selectedPaymentMethod && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('donation.paymentMethod')}</h4>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border">
                <div className="w-8 h-8 bg-gray-300 rounded flex items-center justify-center">
                  <CreditCard size={16} className="text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {selectedPaymentMethod.type === 'card' ? 
                      `••••${selectedPaymentMethod.last4}` : 
                      selectedPaymentMethod.type}
                  </p>
                  <p className="text-sm text-gray-500">{selectedPaymentMethod.brand}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleDonation}
              disabled={isProcessing || getFinalAmount() < 1}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('donation.processing')}</span>
                </>
              ) : (
                <>
                  <Heart size={20} />
                  <span>
                    {selectedPaymentMethod ? 
                      `${t('donation.giveDana')} $${getFinalAmount()}` : 
                      t('donation.continueToPayment')
                    }
                  </span>
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              {t('donation.securePaymentNotice')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 